import Color from 'color';
import { Settings } from '@graphistry/viz/src/containers/settings';
import { container } from '@graphistry/falcor-react-redux';
import { Label as LabelComponent, Labels as LabelsComponent } from '@graphistry/viz/src/components/labels';

import { labelMouseMove } from '@graphistry/viz/src/actions/labels';
import { addFilter, addExclusion } from '@graphistry/viz/src/actions/expressions';
import { 
    filterLabelProperties, 
    processEncodingsComputed,
    processEncodingsNulls 
} from '../utils/encodingUtils';

let MinimalLabel = container({
    renderLoading: false,
    fragment: (fragment) => {
        return (
            `{ type, index, title, globalIndex, columns }`
        );
    },
    dispatchers: {
        onFilter: addFilter,
        onExclude: addExclusion
    }
})(LabelComponent);

let ExpandedLabel = container({
    renderLoading: false,
    fragment: (fragment) => {
        return (
            `{ type, index, title, globalIndex, columns, importantColumns }`
        );
    },
    dispatchers: {
        onFilter: addFilter,
        onExclude: addExclusion
    }
})(LabelComponent)

let Labels = ({
    simulating,
    selectLabel,
    labelMouseMove,
    enabled,
    opacity,
    sceneSelectionType,
    poiEnabled,
    poiLabelEnabled,
    highlightEnabled,
    foreground: { color: color } = {},
    background: { color: background } = {},
    encodings,
    point = [],
    highlight,
    selection,
    propertiesEnabled,
    showActions,
    shortenLabels,
    ...props
}) => {
    let labels = [];
    
    color = new Color(color).toString();
    background = new Color(background).toString();

    if (poiEnabled && point && point.length) {
        labels = point.slice(0);
    }
    if (labelsAreEqual(selection, highlight)) {
        highlight = undefined;
    }
    if (selection) {
        labels = labels.filter(label => !labelsAreEqual(label, selection)).concat(selection);
    }
    if (highlight) {
        labels = labels.filter(label => !labelsAreEqual(label, highlight));
        if (!highlightEnabled || sceneSelectionType || simulating) {
            highlight = undefined;
        } else {
            labels.push(highlight);
        }
    }
    
    const HighlightLabel = highlight && propertiesEnabled ? ExpandedLabel : MinimalLabel;

    return (
        <LabelsComponent
            color={color}
            labels={labels}
            enabled={enabled}
            highlight={highlight}
            selection={selection}
            simulating={simulating}
            poiEnabled={poiEnabled}
            poiLabelEnabled={poiLabelEnabled}
            background={background}
            highlightEnabled={highlightEnabled}
            propertiesEnabled={propertiesEnabled}
            showActions={showActions}
            highlightKey={getLabelKey(highlight)}
            selectionKey={getLabelKey(selection)}
            {...props}>
            {enabled && labels.filter(Boolean).map(label => {
                const LabelType = label === selection ? ExpandedLabel :
                                  label === highlight ? HighlightLabel :
                                                        MinimalLabel;
                return (
                    <LabelType
                        data={label}
                        color={color}
                        opacity={opacity}
                        strokeWidth={props.renderer?.points?.stroke}
                        encodings={encodings}
                        simulating={simulating}
                        background={background}
                        key={getLabelKey(label)}
                        pinned={label === selection}
                        onLabelSelected={selectLabel}
                        onLabelMouseMove={labelMouseMove}
                        isSelectedLabel={label === selection}
                        isHighlightedLabel={label === highlight}
                        hasSelectedLabel={!!selection}
                        hasHighlightedLabel={!!highlight}
                        propertiesEnabled={propertiesEnabled}
                        showActions={showActions}
                        poiLabelEnabled={poiLabelEnabled}
                        sceneSelectionType={sceneSelectionType} 
                        showFull={label === highlight || label === selection}
                        shortenLabels={shortenLabels}
                    />
                )
            }) || []}
        </LabelsComponent>
    );
};

function labelsAreEqual(a, b) {
    return a && b && a.type === b.type && a.globalIndex === b.globalIndex;
}

function getLabelKey(label) {
    return (label && `label-${label.type}-${label.index}-${label.globalIndex}`) || 'no-label';
}

const BADGE_TYPES = 'badgeTopLeft, badgeLeft, badgeBottomLeft, badgeBottom, badgeBottomRight, badgeRight, badgeTopRight, badgeTop, badgeCover';

Labels = container({
    renderLoading: false,
    fragment: ({ point = [], settings, highlight, selection, propertiesEnabled } = {}) => `{
        id, name, opacity, timeZone,
        enabled, poiEnabled, poiLabelEnabled, highlightEnabled, poiMax, propertiesEnabled, showActions, shortenLabels,
        renderer: {
            background: { color },
            points: { stroke }
        },
        ['background', 'foreground']: { color },
        ...${Settings.fragment({ settings })},
        point: ${MinimalLabel.fragments(point)},
        selection: ${ExpandedLabel.fragment(selection)},
        highlight: ${(propertiesEnabled ? ExpandedLabel : MinimalLabel).fragment(highlight)},
        encodings: {
            point: { color, size, icon, ${ BADGE_TYPES }, fieldinclusion, fieldexclusion, computed, types, show, value }, 
            edge: { color, icon, ${ BADGE_TYPES }, fieldinclusion, fieldexclusion, computed, types, show, value }
        }
    }`,
    dispatchers: {
        labelMouseMove
    }
})(Labels);

export { Labels, MinimalLabel };
